import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Phone, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { COMPANY } from "../lib/constants";
import { useLang } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const location = useLocation();
  const formRef = useRef(null);
  const { t } = useLang();

  // Service options in current language + "Other" option
  const serviceOptions = [...t.services.items.map((s) => s.title), t.services.otherOption];

  // If user navigated from a service card, use the index so we display the
  // option in the current language regardless of which language they clicked.
  const initialServiceIndex =
    typeof location.state?.serviceIndex === "number" ? location.state.serviceIndex : null;

  const [form, setForm] = useState(() => ({
    name: "",
    phone: "",
    email: "",
    service:
      initialServiceIndex !== null
        ? serviceOptions[initialServiceIndex]
        : location.state?.service || "",
    message: "",
  }));
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // When language changes, re-map the selected service to the equivalent option
  // in the new language (so the dropdown doesn't go blank on toggle).
  useEffect(() => {
    if (!form.service) return;
    // Find current option index by matching against EN or ES titles
    const allTitles = serviceOptions;
    if (allTitles.includes(form.service)) return; // already in this language
    // try other-language fallback: find by index from previous serviceIndex
    if (initialServiceIndex !== null && serviceOptions[initialServiceIndex]) {
      setForm((f) => ({ ...f, service: serviceOptions[initialServiceIndex] }));
    } else {
      setForm((f) => ({ ...f, service: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    if (location.state?.service || typeof location.state?.serviceIndex === "number") {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
  }, [location.state]);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service || !form.message) {
      toast.error(t.contact.validationError);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, form);
      setSuccess(true);
      toast.success(t.contact.successToast);
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.contact.errorToast);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="section-y border-b border-border" data-testid="contact-hero">
        <div className="container-x max-w-4xl">
          <span className="overline">{t.contact.overline}</span>
          <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            {t.contact.title}
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl">{t.contact.subtitle}</p>
        </div>
      </section>

      <section className="section-y" data-testid="contact-section">
        <div className="container-x grid lg:grid-cols-[1fr_1.4fr] gap-12 items-start">
          {/* Info column */}
          <aside className="space-y-8 lg:sticky lg:top-28" data-testid="contact-info">
            <div className="rounded-2xl border border-border bg-card p-7">
              <h2 className="font-display font-bold text-2xl">{t.contact.talkTitle}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{t.contact.talkDesc}</p>
              <a
                href={COMPANY.phoneHref}
                className="mt-5 inline-flex items-center gap-3 text-2xl sm:text-3xl font-display font-bold text-primary hover:underline"
                data-testid="contact-phone-link"
              >
                <Phone size={26} /> {COMPANY.phoneDisplay}
              </a>
            </div>

            <div className="rounded-2xl border border-border bg-card p-7">
              <div className="flex items-center gap-2">
                <MapPin size={18} className="text-primary" />
                <h3 className="font-display font-bold text-lg">{t.contact.serviceAreaTitle}</h3>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                {t.contact.serviceAreaDescPre}
                <span className="font-semibold text-foreground">{t.contact.serviceAreaBold}</span>
                {t.contact.serviceAreaDescPost}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {COMPANY.cities.map((c) => (
                  <span
                    key={c}
                    className="text-xs font-medium px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20"
                    data-testid={`city-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-7">
              <h3 className="font-display font-bold text-lg">{t.contact.expectTitle}</h3>
              <ul className="mt-4 space-y-3 text-sm">
                {t.contact.expect.map((line, i) => (
                  <li key={i} className="flex gap-3">
                    <CheckCircle2 size={18} className="text-primary shrink-0 mt-0.5" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Form column */}
          <div ref={formRef} id="quote-form" className="rounded-2xl border border-border bg-card p-7 sm:p-9" data-testid="quote-form-card">
            {success ? (
              <div className="text-center py-10" data-testid="quote-success">
                <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 text-primary grid place-items-center">
                  <CheckCircle2 size={32} />
                </div>
                <h2 className="mt-6 font-display font-bold text-2xl">{t.contact.successTitle}</h2>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto">{t.contact.successDesc}</p>
                <a href={COMPANY.phoneHref} className="mt-6 inline-block" data-testid="success-call-link">
                  <Button className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 rounded-full h-12 px-6 font-semibold">
                    <Phone size={18} /> {t.common.call} {COMPANY.phoneDisplay}
                  </Button>
                </a>
                <button
                  type="button"
                  onClick={() => setSuccess(false)}
                  className="block mx-auto mt-6 text-sm text-muted-foreground hover:text-foreground underline"
                  data-testid="success-new-quote"
                >
                  {t.contact.submitAnother}
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="quote-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t.contact.labels.name}</Label>
                    <Input
                      id="name"
                      data-testid="form-name"
                      placeholder={t.contact.placeholders.name}
                      value={form.name}
                      onChange={(e) => onChange("name", e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t.contact.labels.phone}</Label>
                    <Input
                      id="phone"
                      data-testid="form-phone"
                      type="tel"
                      placeholder={t.contact.placeholders.phone}
                      value={form.phone}
                      onChange={(e) => onChange("phone", e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t.contact.labels.email}</Label>
                  <Input
                    id="email"
                    data-testid="form-email"
                    type="email"
                    placeholder={t.contact.placeholders.email}
                    value={form.email}
                    onChange={(e) => onChange("email", e.target.value)}
                    required
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service">{t.contact.labels.service}</Label>
                  <Select value={form.service} onValueChange={(v) => onChange("service", v)}>
                    <SelectTrigger id="service" data-testid="form-service" className="h-11">
                      <SelectValue placeholder={t.contact.placeholders.service} />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((opt) => (
                        <SelectItem
                          key={opt}
                          value={opt}
                          data-testid={`service-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">{t.contact.labels.message}</Label>
                  <Textarea
                    id="message"
                    data-testid="form-message"
                    placeholder={t.contact.placeholders.message}
                    value={form.message}
                    onChange={(e) => onChange("message", e.target.value)}
                    required
                    rows={5}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="form-submit"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-12 font-semibold text-base gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> {t.contact.submitting}
                    </>
                  ) : (
                    t.contact.submit
                  )}
                </Button>

                <p className="text-xs text-muted-foreground text-center">{t.contact.footnote}</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
